export const OTHER_PERMISSIONS_MODULE_NAME = 'Other Permissions';

export const SPECIAL_PERMISSION_ACTION_NAMES = {
  EDIT_OLDER_SHIPMENTS: 'edit shipments created before 6 months',
  EDIT_OLDER_BAGS: 'edit bags created before 6 months',
  EDIT_OLDER_MAWBS: 'edit mawbs created before 6 months',
  EDIT_OLDER_MANIFESTS: 'edit manifests created before 6 months',
  EDIT_MAWB_DATE_EXTENDED:
    'create / edit mawb date to more than 1-month window',
  EDIT_MANIFEST_DATE_EXTENDED:
    'create / edit manifest date to more than 1-month window',
  HANDLE_SPAIN_SHIPMENTS: 'handle shipments coming from spain',
  HANDLE_HIGH_VALUE_SHIPMENTS:
    'handle shipments with total value more than $5000',
  GATEWAY_UNCLEARED_CBP_SHIPMENTS: 'gateway uncleared shipments from cbp',
  /** Legacy API action name alias when catalog label differs by environment. */
  GATEWAY_HELD_CBP_SHIPMENTS: 'gateway held shipments from cbp',
} as const;

export type SpecialPermissionActionName =
  (typeof SPECIAL_PERMISSION_ACTION_NAMES)[keyof typeof SPECIAL_PERMISSION_ACTION_NAMES];

export const SPECIAL_PERMISSION_ACTION_IDS = {
  EDIT_OLDER_SHIPMENTS: 208,
  EDIT_OLDER_MAWBS: 210,
  EDIT_OLDER_MANIFESTS: 211,
  EDIT_MAWB_DATE_EXTENDED: 212,
  EDIT_MANIFEST_DATE_EXTENDED: 213,
  HANDLE_SPAIN_SHIPMENTS: 214,
  EDIT_OLDER_BAGS: 215,
  HANDLE_HIGH_VALUE_SHIPMENTS: 1249,
} as const;

export const rolesAndPermissionsSpecialEditGuardBannerMessage =
  'Uncheck the Special edit permission for old records before trying to uncheck the Edit and Bulk Edit';

export const rolesAndPermissionsRevokeSpecialEditConfirmationMessage =
  'Would you like to revoke the normal edit and bulk edit as well?';

export const SPECIAL_PERMISSION_MODULE_SYNC: Readonly<
  Record<
    string,
    {
      targetModule: string;
      syncType: 'older-record' | 'extended-date';
    }
  >
> = {
  [SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_SHIPMENTS]: {
    targetModule: 'Shipment',
    syncType: 'older-record',
  },
  [SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_BAGS]: {
    targetModule: 'Bag',
    syncType: 'older-record',
  },
  [SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_MAWBS]: {
    targetModule: 'MAWB',
    syncType: 'older-record',
  },
  [SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_MANIFESTS]: {
    targetModule: 'Manifest',
    syncType: 'older-record',
  },
  [SPECIAL_PERMISSION_ACTION_NAMES.EDIT_MAWB_DATE_EXTENDED]: {
    targetModule: 'MAWB',
    syncType: 'extended-date',
  },
  [SPECIAL_PERMISSION_ACTION_NAMES.EDIT_MANIFEST_DATE_EXTENDED]: {
    targetModule: 'Manifest',
    syncType: 'extended-date',
  },
};

export const MODULE_TO_OLDER_SPECIAL_PERMISSION: Readonly<
  Record<string, SpecialPermissionActionName>
> = {
  Shipment: SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_SHIPMENTS,
  Bag: SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_BAGS,
  Manifest: SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_MANIFESTS,
  MAWB: SPECIAL_PERMISSION_ACTION_NAMES.EDIT_OLDER_MAWBS,
};

/** R&P six-month edit-validity info banner (FR-RAP-026). */
export const SPECIAL_PERMISSIONS_EDIT_VALIDITY_INFO_BANNERS: Readonly<
  Record<string, string>
> = {
  Shipment:
    'Edit and Bulk edit provided here is valid for shipments created in last 6 months only. To edit shipments older than that, go to Other Permissions',
  Bag: 'Edit and Bulk edit provided here is valid for Bags created in last 6 months only. To edit Bags older than that, go to Other Permissions',
  Manifest:
    'Edit and Bulk edit provided here is valid for Manifests created in last 6 months only. To edit Manifests older than that, go to Other Permissions',
  MAWB: 'Edit and Bulk edit provided here is valid for MAWBs created in last 6 months only. To edit MAWBs older than that, go to Other Permissions',
};

/** Tooltip when row `isAllowedToEdit` is false (operational Edit/Bulk Edit icons). */
export const SPECIAL_PERMISSIONS_EDIT_VALIDITY_TOOLTIPS: Readonly<
  Record<string, string>
> = {
  Shipment:
    'You need special permissions to edit shipments older than 6 months',
  Bag: 'You need special permissions to edit Bags older than 6 months',
  Manifest:
    'You need special permissions to edit Manifests older than 6 months',
  MAWB: 'You need special permissions to edit MAWBs older than 6 months',
};

export function getSpecialPermissionsEditValidityTooltip(
  moduleName: string
): string {
  return (
    SPECIAL_PERMISSIONS_EDIT_VALIDITY_TOOLTIPS[moduleName] ??
    SPECIAL_PERMISSIONS_EDIT_VALIDITY_TOOLTIPS['Shipment']
  );
}

export const SPECIAL_PERMISSIONS_SPAIN_MUTATING_MESSAGE =
  'You need special permissions to handle shipments coming from Spain';

export const SPECIAL_PERMISSIONS_HIGH_VALUE_MUTATING_MESSAGE =
  'You need special permissions to handle shipments valued above $5000';

export const SPECIAL_PERMISSIONS_GATEWAY_UNCLEARED_CBP_MESSAGE =
  'The shipment has not been cleared by the CBP. You will need special permissions to gateway these shipments';

export const SPAIN_ORIGIN_AIRPORT_CODES = ['TFN', 'TFS'] as const;

export const HIGH_VALUE_SHIPMENT_THRESHOLD = 5000;

export type ShipmentMutatingBlockReason = 'age' | 'spain' | 'high_value';

export function getShipmentMutatingBlockMessage(
  reason: ShipmentMutatingBlockReason
): string {
  switch (reason) {
    case 'spain':
      return SPECIAL_PERMISSIONS_SPAIN_MUTATING_MESSAGE;
    case 'high_value':
      return SPECIAL_PERMISSIONS_HIGH_VALUE_MUTATING_MESSAGE;
    default:
      return getSpecialPermissionsEditValidityTooltip('Shipment');
  }
}

/** Header for SPE bulk-edit eligibility popups (partial / none); not the standard bulk-edit confirm. */
export const BULK_EDIT_ELIGIBILITY_POPUP_HEADER = 'Confirmation';
